import React from 'react';
import { Typography, Box } from '@mui/material';
import { GridRenderCellParams } from '@mui/x-data-grid-premium';
import { Link } from 'react-router-dom';
import { formatNumber } from './customFormat';
import { getDateValue, renderDateCell } from './dataGridUtils';
import { StatusChip } from '../components/UI/StatusChip';

// Mapping between health check names (from backend) and table column fields
export const healthCheckToFieldMap: Record<string, string> = {
  // Customer Status checks
  'Missing Status': 'customer_status',
  
  // Customer Critical data checks
  'Missing Fulfillment Group': 'active_fg_count',
  'Missing Data Format': 'delivery_data_format',
  'Missing Fulfillment Destination': 'fulfillment_destination_type',
  
  // Customer Urgent delivery date checks
  'Missing Recent Delivery Date': 'most_recent_delivery',
  'Invalid Recent Delivery Date': 'most_recent_delivery',
  'Missing Next Scheduled Delivery': 'next_scheduled_delivery',
  'Invalid Next Scheduled Delivery': 'next_scheduled_delivery',
  
  // Customer Urgent license checks
  'Missing License End Date': 'next_license_end_date',
  'Invalid License End Date': 'next_license_end_date',
};

// Health check categories with their descriptions and specific checks
export const HEALTH_CHECK_CATEGORIES = {
  delivery_timeline: {
    title: 'Delivery Timeline Issues',
    description: 'Validates delivery dates are present, in correct sequence, and logically consistent',
    checks: [
      'First delivery date must not be in the future when completions exist',
      'Most recent delivery must not be in the future',
      'Active deliveries must have completed at least one delivery AND have scheduled fulfillments',
      'Active deliveries must have a next delivery date scheduled',
      'Next delivery date must not be in the past',
    ]
  },
  license_status: {
    title: 'License & Status Consistency',
    description: 'Ensures delivery status aligns with license dates and scheduled activities',
    checks: [
      'License end date must be present',
      'Active deliveries cannot have expired licenses',
      'Final scheduled delivery must not be after license end date',
      'Active deliveries with past final delivery dates should be inactive',
    ]
  },
  volume_upgrades: {
    title: 'Volume Upgrade Scheduling',
    description: 'Validates volume upgrade schedules align with delivery plans',
    checks: [
      'Active deliveries qualified for upgrades must have next upgrade scheduled',
      'Next volume upgrade date must not be in the past for active deliveries',
      'Deliveries not approved for upgrades should not have upgrades scheduled',
    ]
  },
  fulfillment_setup: {
    title: 'Fulfillment Setup',
    description: 'Verifies delivery has necessary operational configuration',
    checks: [
      'Scheduled fulfillments count must be present',
      'Scheduled volume upgrades count must be present (if qualified)',
      'Fulfillment groups must be assigned',
      'Fulfillment destination must be specified',
    ]
  },
  suspicious_inactive: {
    title: 'Suspicious Inactive Deliveries',
    description: 'Identifies inactive deliveries that may have been improperly deactivated',
    checks: [
      'Inactive deliveries with future licenses and fulfillment groups still assigned',
      'Inactive deliveries with past delivery activity but no schedule removal',
    ]
  }
} as const;

// Mapping for delivery health checks
// Maps health check name -> field name (the field that has the issue)
export const deliveryHealthCheckToFieldMap: Record<string, string> = {
  // First delivery checks (checks first_delivery_date field)
  'Most Recent Delivery with Future First Date': 'first_delivery_date',
  
  // Most recent delivery checks (checks most_recent_delivery_date field)
  'Most Recent Delivery in the Future': 'most_recent_delivery_date',
  'Active Delivery Never Completed': 'most_recent_delivery_date',
  
  // Next delivery checks
  'Missing Next Delivery for Active': 'next_delivery_date',
  'Invalid Next Delivery (In Past)': 'next_delivery_date',
  
  // Final scheduled delivery checks
  'Invalid Final Scheduled Delivery (After License End)': 'final_scheduled_delivery',
  'Invalid Active Status with Past Final Delivery': 'final_scheduled_delivery',
  
  // License checks
  'Missing License End Date': 'license_end_date',
  'Invalid Active Status with Expired License': 'license_end_date',
  
  // Volume upgrade checks
  'Missing Next Volume Upgrade for Active': 'next_volume_upgrade',
  'Invalid Next Volume Upgrade (In Past for Active)': 'next_volume_upgrade',
  'Invalid Volume Upgrades Not Approved but Scheduled': 'count_volume_upgrades_on_schedule',
  
  // Count checks
  'Missing Scheduled Fulfillments Count': 'count_fulfillments_on_schedule',
  'Missing Scheduled Volume Upgrades Count': 'count_volume_upgrades_on_schedule',
  
  // Fulfillment group and destination checks
  'Missing Fulfillment Groups': 'fulfillment_groups',
  'Missing Fulfillment Destination': 'fulfillment_destination',
  
  // Suspicious inactive check
  'Suspicious Inactive Delivery': 'license_end_date',
};

/**
 * Check if a customer has a health check issue for a specific field.
 * Uses the backend's health_issues array as the single source of truth.
 */
export const customerHasIssueInField = (
  healthIssues: string[] | undefined,
  fieldName: string
): boolean => {
  if (!healthIssues) return false;
  
  return healthIssues.some(checkName => 
    healthCheckToFieldMap[checkName] === fieldName
  );
};

/**
 * Check if a delivery has a health check issue for a specific field.
 * Uses the backend's health_issues array as the single source of truth.
 */
export const deliveryHasIssueInField = (
  healthIssues: string[] | undefined,
  fieldName: string
): boolean => {
  if (!healthIssues) return false;
  
  return healthIssues.some(checkName => 
    deliveryHealthCheckToFieldMap[checkName] === fieldName
  );
};

// Helper to render text with conditional styling
export const renderHealthText = (
  value: any, 
  hasIssue: boolean = false, 
  formatter?: (val: any) => string
) => {
  const displayValue = formatter 
    ? formatter(value) 
    : (value ?? 'Missing');
  
  return (
    <Typography
      variant="body2"
      sx={{
        color: hasIssue ? 'error.main' : 'text.primary',
        fontWeight: hasIssue ? 'bold' : 'normal',
      }}
    >
      {displayValue}
    </Typography>
  );
};

// Standard health check renderers
export const healthCheckRenderers = {
  // Customer ID with link
  customerIdLink: (params: GridRenderCellParams) => (
    <Link to={`/customer/${params.value}`}>{params.value}</Link>
  ),

  // Number - uses backend's health_issues to determine if there's an issue
  numberWithMissingCheck: (params: GridRenderCellParams) => {
    const value = params.value as number;
    const healthIssues = params.row.health_issues;
    const hasIssue = customerHasIssueInField(healthIssues, params.field);
    return renderHealthText(value, hasIssue, formatNumber);
  },

  // Count - uses backend's health_issues to determine if there's an issue
  countWithZeroIssue: (params: GridRenderCellParams) => {
    const count = params.value as number;
    const healthIssues = params.row.health_issues;
    const hasIssue = customerHasIssueInField(healthIssues, params.field);
    return renderHealthText(count, hasIssue, formatNumber);
  },

  // String - uses backend's health_issues to determine if there's an issue
  stringWithMissingCheck: (params: GridRenderCellParams) => {
    const value = params.value as string;
    const healthIssues = params.row.health_issues;
    const hasIssue = customerHasIssueInField(healthIssues, params.field);
    return renderHealthText(value, hasIssue);
  },

  // Date - uses backend's health_issues to determine if there's an issue
  dateWithMissingCheck: (params: GridRenderCellParams) => {
    const hasValue = params.value != null;
    const healthIssues = params.row.health_issues;
    const hasIssue = customerHasIssueInField(healthIssues, params.field);
    
    if (!hasValue) {
      return renderHealthText('Missing', hasIssue);
    }
    
    const dateElement = renderDateCell(params);
    return <Box sx={{ color: hasIssue ? 'error.main' : 'text.primary' }}>{dateElement}</Box>;
  },

  // Status chip only (for counts in summary tables)
  statusChipOnly: (params: GridRenderCellParams) => {
    const count = params.value as number;
    if (count === 0) return null;
    return <StatusChip type="issue" />;
  },

  // Combined number + status chip
  numberWithStatusChip: (params: GridRenderCellParams) => {
    const count = params.value as number;
    const hasIssue = count > 0; // For summary tables where count = number of issues
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {renderHealthText(count, hasIssue, formatNumber)}
        {hasIssue ? <StatusChip type="issue" /> : <StatusChip type="ok" />}
      </Box>
    );
  },
};

// Delivery-specific health check renderers
export const deliveryHealthCheckRenderers = {
  // Delivery ID with link
  deliveryIdLink: (params: GridRenderCellParams) => (
    <Link to={`/delivery/${params.value}`}>{params.value}</Link>
  ),

  // Number - uses backend's health_issues to determine if there's an issue
  numberWithMissingCheck: (params: GridRenderCellParams) => {
    const value = params.value as number;
    const healthIssues = params.row.health_issues;
    const hasIssue = deliveryHasIssueInField(healthIssues, params.field);
    return renderHealthText(value, hasIssue, formatNumber);
  },

  // Count - uses backend's health_issues to determine if there's an issue
  countWithZeroIssue: (params: GridRenderCellParams) => {
    const count = params.value as number;
    const healthIssues = params.row.health_issues;
    const hasIssue = deliveryHasIssueInField(healthIssues, params.field);
    return renderHealthText(count, hasIssue, formatNumber);
  },

  // String - uses backend's health_issues to determine if there's an issue
  stringWithMissingCheck: (params: GridRenderCellParams) => {
    const value = params.value as string;
    const healthIssues = params.row.health_issues;
    const hasIssue = deliveryHasIssueInField(healthIssues, params.field);
    return renderHealthText(value, hasIssue);
  },

  // Date - uses backend's health_issues to determine if there's an issue
  dateWithMissingCheck: (params: GridRenderCellParams) => {
    const hasValue = params.value != null;
    const healthIssues = params.row.health_issues;
    const hasIssue = deliveryHasIssueInField(healthIssues, params.field);
    
    if (!hasValue) {
      return renderHealthText('Missing', hasIssue);
    }
    
    const dateElement = renderDateCell(params);
    return <Box sx={{ 
      color: hasIssue ? 'error.main' : 'text.primary',
      fontWeight: hasIssue ? 'bold' : 'normal',
    }}>{dateElement}</Box>;
  },

  // Boolean - uses backend's health_issues to determine if there's an issue
  booleanWithIssueCheck: (params: GridRenderCellParams) => {
    const value = params.value as boolean;
    const healthIssues = params.row.health_issues;
    const hasIssue = deliveryHasIssueInField(healthIssues, params.field);
    const displayValue = value ? 'Active' : 'Inactive';
    return renderHealthText(displayValue, hasIssue);
  },
};

